import {create, fromJson, type DescMessage, type MessageShape} from '@github-ui/dotcom-schema/protobuf'
import {
  type RepoPullRequest,
  RepoPullRequestSchema,
  RepoPullRequestDisplayState,
  RepoPullRequestReviewDecision,
  RepoPullRequestState,
  RepoPullRequestStatusCheckState,
  type RepoPullsDashboardContentResponse,
  RepoPullsDashboardContentResponseSchema,
  type RepoPullsDashboardDeferredResponse,
  RepoPullsDashboardDeferredResponseSchema,
  type RepoPullsDashboardLayoutResponse,
  RepoPullsDashboardLayoutResponseSchema,
} from '@github-ui/dotcom-schema/types/pull_requests/v1/repo_pulls_dashboard'
import {queryFnFetchWithProtoSignal} from '@github-ui/react-core/future/query-fn-fetch'
import {
  DisplayStateType,
  type EnrichedPullListItem,
  ReviewDecisionState,
  StateType,
  StatusCheckState,
  type DeferredItem,
  type PullItem,
  type PullListItem,
} from '@github-ui/shared-dashboard-lists/pulls/types/list-types'

export type LegacyRepoPullsDashboardContentPayload = {
  results: PullItem[]
  currentPage: number
  totalPages: number
  totalCount: number
  openCount?: number
  closedCount?: number
  error?: string | null
}

export type LegacyRepoPullsDashboardDeferredPayload = {
  results: DeferredItem[]
  error?: string | null
}

export type LegacyRepoPullsDashboardLayoutPayload = {
  compactMode?: boolean
  sidebarExpanded?: boolean
  actions?: {
    labels?: {url: string; count: number}
    milestones?: {url: string; count: number}
    newPullRequest?: {label: string; url?: string; disabledReason?: string}
    agentTaskRepository?: {
      id: number
      name: string
      ownerLogin: string
      defaultBranch: string
      private: boolean
      createdAt: string
      currentUserCanPush: boolean
      isFork: boolean
      isEmpty: boolean
      ownerAvatar: string
      public: boolean
      isOrgOwned: boolean
    }
  }
}

const DISPLAY_STATES: Record<string, RepoPullRequestDisplayState> = {
  DRAFT: RepoPullRequestDisplayState.DRAFT,
  OPEN: RepoPullRequestDisplayState.OPEN,
  CLOSED: RepoPullRequestDisplayState.CLOSED,
  MERGED: RepoPullRequestDisplayState.MERGED,
  QUEUED: RepoPullRequestDisplayState.QUEUED,
}

const STATES: Record<string, RepoPullRequestState> = {
  OPEN: RepoPullRequestState.OPEN,
  CLOSED: RepoPullRequestState.CLOSED,
  MERGED: RepoPullRequestState.MERGED,
}

const REVIEW_DECISIONS: Record<string, RepoPullRequestReviewDecision> = {
  APPROVED: RepoPullRequestReviewDecision.APPROVED,
  CHANGES_REQUESTED: RepoPullRequestReviewDecision.CHANGES_REQUESTED,
  REVIEW_REQUIRED: RepoPullRequestReviewDecision.REQUIRED,
  approved: RepoPullRequestReviewDecision.APPROVED,
  changes_requested: RepoPullRequestReviewDecision.CHANGES_REQUESTED,
  required: RepoPullRequestReviewDecision.REQUIRED,
}

const STATUS_CHECK_STATES: Record<string, RepoPullRequestStatusCheckState> = {
  SUCCESS: RepoPullRequestStatusCheckState.SUCCESS,
  FAILURE: RepoPullRequestStatusCheckState.FAILURE,
  PENDING: RepoPullRequestStatusCheckState.PENDING,
}

function legacyPullToProto(pull: PullItem): RepoPullRequest {
  return create(RepoPullRequestSchema, {
    assignees: pull.assignees?.map(assignee => ({login: assignee.login, avatarUrl: assignee.avatarUrl})) ?? [],
    author: {displayLogin: pull.author.displayLogin},
    commentCount: pull.commentCount,
    commitHeadShaChannel: pull.commitHeadShaChannel,
    agentAttributedUser: pull.agentAttributedUser ? {displayLogin: pull.agentAttributedUser.displayLogin} : undefined,
    createdAt: pull.createdAt,
    updatedAt: pull.updatedAt,
    displayState: pull.displayState
      ? (DISPLAY_STATES[pull.displayState] ?? RepoPullRequestDisplayState.UNSPECIFIED)
      : RepoPullRequestDisplayState.UNSPECIFIED,
    headSha: pull.headSha,
    labels: pull.labels.map(label => ({
      id: BigInt(label.id),
      name: label.name,
      color: label.color,
      description: label.description,
      nameHtml: label.nameHtml ? {value: label.nameHtml} : undefined,
    })),
    id: BigInt(pull.id),
    globalId: pull.globalId,
    isDraft: pull.isDraft,
    isReadByCurrentUser: pull.isReadByCurrentUser,
    number: pull.number,
    permalink: pull.permalink,
    repoNameWithOwner: pull.repoNameWithOwner,
    stackPosition: pull.stackPosition ?? undefined,
    stackSize: pull.stackSize ?? undefined,
    state: STATES[pull.state] ?? RepoPullRequestState.UNSPECIFIED,
    title: pull.title,
    titleHtml: pull.titleHtml ? {value: pull.titleHtml} : undefined,
  })
}

export function normalizeLegacyRepoPullsContent(
  legacy: LegacyRepoPullsDashboardContentPayload,
): RepoPullsDashboardContentResponse {
  return create(RepoPullsDashboardContentResponseSchema, {
    results: legacy.results.map(legacyPullToProto),
    currentPage: legacy.currentPage,
    totalPages: legacy.totalPages,
    totalCount: legacy.totalCount,
    error: legacy.error ?? undefined,
    openCount: legacy.openCount,
    closedCount: legacy.closedCount,
  })
}

export function normalizeLegacyRepoPullsLayout(
  legacy: LegacyRepoPullsDashboardLayoutPayload,
): RepoPullsDashboardLayoutResponse {
  return create(RepoPullsDashboardLayoutResponseSchema, {
    compactMode: legacy.compactMode ?? false,
    sidebarExpanded: legacy.sidebarExpanded,
    actions: legacy.actions
      ? {
          labels: legacy.actions.labels,
          milestones: legacy.actions.milestones,
          newPullRequest: legacy.actions.newPullRequest,
          agentTaskRepository: legacy.actions.agentTaskRepository
            ? {...legacy.actions.agentTaskRepository, id: BigInt(legacy.actions.agentTaskRepository.id)}
            : undefined,
        }
      : undefined,
  })
}

export function normalizeLegacyRepoPullsDeferred(
  legacy: LegacyRepoPullsDashboardDeferredPayload,
): RepoPullsDashboardDeferredResponse {
  return create(RepoPullsDashboardDeferredResponseSchema, {
    results: legacy.results.map(result => ({
      id: BigInt(result.id),
      globalId: result.globalId,
      reviewDecisionState: result.reviewDecisionState
        ? (REVIEW_DECISIONS[result.reviewDecisionState] ?? RepoPullRequestReviewDecision.UNSPECIFIED)
        : RepoPullRequestReviewDecision.UNSPECIFIED,
      statusCheckRollup: result.statusCheckRollup
        ? {
            state: STATUS_CHECK_STATES[result.statusCheckRollup.state] ?? RepoPullRequestStatusCheckState.UNSPECIFIED,
            totalCount: result.statusCheckRollup.totalCount,
            successCount: result.statusCheckRollup.successCount,
          }
        : undefined,
      approvedReviewsCount: result.approvedReviewsCount,
      changesRequestedReviewsCount: result.changesRequestedReviewsCount,
    })),
    error: legacy.error ?? undefined,
  })
}

function displayState(displayStateValue: RepoPullRequestDisplayState): PullListItem['displayState'] {
  switch (displayStateValue) {
    case RepoPullRequestDisplayState.DRAFT:
      return DisplayStateType.Draft
    case RepoPullRequestDisplayState.OPEN:
      return DisplayStateType.Opened
    case RepoPullRequestDisplayState.CLOSED:
      return DisplayStateType.Closed
    case RepoPullRequestDisplayState.MERGED:
      return DisplayStateType.Merged
    case RepoPullRequestDisplayState.QUEUED:
      return DisplayStateType.Queued
    default:
      return undefined
  }
}

function pullState(stateValue: RepoPullRequestState): PullListItem['state'] {
  switch (stateValue) {
    case RepoPullRequestState.CLOSED:
      return StateType.Closed
    case RepoPullRequestState.MERGED:
      return StateType.Merged
    default:
      return StateType.Opened
  }
}

function reviewDecision(decision: RepoPullRequestReviewDecision) {
  switch (decision) {
    case RepoPullRequestReviewDecision.APPROVED:
      return ReviewDecisionState.Approved
    case RepoPullRequestReviewDecision.CHANGES_REQUESTED:
      return ReviewDecisionState.ChangesRequested
    case RepoPullRequestReviewDecision.REQUIRED:
      return ReviewDecisionState.ReviewRequired
    default:
      return undefined
  }
}

function statusCheckState(stateValue: RepoPullRequestStatusCheckState) {
  switch (stateValue) {
    case RepoPullRequestStatusCheckState.SUCCESS:
      return StatusCheckState.Success
    case RepoPullRequestStatusCheckState.FAILURE:
      return StatusCheckState.Failure
    case RepoPullRequestStatusCheckState.PENDING:
      return StatusCheckState.Pending
    default:
      return undefined
  }
}

function pullToListItem(pull: RepoPullRequest): PullListItem {
  return {
    id: pull.id,
    globalId: pull.globalId,
    number: pull.number,
    title: pull.title,
    titleHtml: pull.titleHtml,
    permalink: pull.permalink ?? '',
    repoNameWithOwner: pull.repoNameWithOwner ?? '',
    assignees: pull.assignees,
    author: {displayLogin: pull.author?.displayLogin ?? ''},
    agentAttributedUser: pull.agentAttributedUser,
    createdAt: pull.createdAt,
    updatedAt: pull.updatedAt,
    commentCount: pull.commentCount,
    labels: pull.labels,
    isReadByCurrentUser: pull.isReadByCurrentUser,
    isDraft: pull.isDraft,
    state: pullState(pull.state),
    displayState: displayState(pull.displayState),
    stackPosition: pull.stackPosition ?? null,
    stackSize: pull.stackSize ?? null,
    headSha: pull.headSha,
    commitHeadShaChannel: pull.commitHeadShaChannel,
  }
}

function deferredIdentity(item: {id: number | bigint; globalId?: string}) {
  return item.globalId || `pull_request-${item.id}`
}

export function repoPullsToListItems(
  results: RepoPullRequest[],
  deferredResults: RepoPullsDashboardDeferredResponse['results'] = [],
): EnrichedPullListItem[] {
  const deferredById = new Map(deferredResults.map(result => [deferredIdentity(result), result]))
  return results.map(result => {
    const pull = pullToListItem(result)
    const deferred = deferredById.get(deferredIdentity(result))
    const rollupState = deferred?.statusCheckRollup && statusCheckState(deferred.statusCheckRollup.state)
    return {
      ...pull,
      reviewDecisionState: deferred ? reviewDecision(deferred.reviewDecisionState) : undefined,
      statusCheckRollup:
        deferred?.statusCheckRollup && rollupState
          ? {
              state: rollupState,
              totalCount: deferred.statusCheckRollup.totalCount,
              successCount: deferred.statusCheckRollup.successCount,
            }
          : undefined,
      approvedReviewsCount: deferred?.approvedReviewsCount,
      changesRequestedReviewsCount: deferred?.changesRequestedReviewsCount,
    }
  })
}

type RouteQueryKey = Parameters<typeof queryFnFetchWithProtoSignal>[0]

async function fetchReactRoutePayload<Desc extends DescMessage, Legacy>(
  queryKey: RouteQueryKey,
  routeId: string,
  schema: Desc,
  adapt: (legacy: Legacy) => MessageShape<Desc>,
): Promise<MessageShape<Desc>> {
  const {json, isProtoJson} = await queryFnFetchWithProtoSignal<{payload?: Record<string, unknown>}>(queryKey)
  const payload = json.payload?.[routeId]
  if (!payload) throw new Error(`Unable to find payload for route Id: ${routeId}`)
  return isProtoJson
    ? fromJson({schema, raw: payload, routeId, queryName: queryKey.queryName ?? 'unknown'})
    : adapt(payload as Legacy)
}

export function repoPullsDeferredQueryFn(queryKey: RouteQueryKey): Promise<RepoPullsDashboardDeferredResponse> {
  return fetchReactRoutePayload(
    queryKey,
    'repoPullsDashboardDeferredRoute',
    RepoPullsDashboardDeferredResponseSchema,
    normalizeLegacyRepoPullsDeferred,
  )
}
